import { take, put, spawn, takeLatest, fork } from 'redux-saga/effects'
import { handleRequest } from './requests'
import { STATE_KEYS } from '../state/requests'
import { showSnackBar } from '../services/toast'
import i18n from 'config/i18n'
import {
    AmbassadorActions,
    GetAmbassadorBrandsPayload,
    saveAmbassadorBrands,
    GetAmbassadorFiltersPayload,
    GetAmbassadorListPayload,
    saveAmbassadorList,
    GetBrandContentTypesPayload,
    saveBrandContentTypes,
    UpdateAmbassadorFavoritePayload,
    updateAmbassadorFavoriteSuccess,
} from '../state/ambassadors'
import {
    updateAmbassadorFavorite,
    UpdateAmbassadorFavoriteRequest,
    UpdateAmbassadorFavoriteResponse,
    getAmbassadorBrands,
    GetAmbassadorBrandsRequest,
    GetAmbassadorBrandsResponse,
    getBrandContentTypes,
    GetBrandContentTypesRequest,
    BrandContentType,
    GetAmbassadorFiltersRequest,
    getAmbassadorFilters,
    getAmbassadorList,
    GetAmbassadorListRequest,
    AmbassadorListResponse,
    FilterResponse,
    getPlatformIdFromContentType,
} from '../services/api/ambassadors'
import { saveAmbassadorFilters } from 'state/filter'
import { Action } from 'redux'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

export function* handleUpdateAmbassadorFavorite(payload: UpdateAmbassadorFavoritePayload): any {
    yield handleRequest<UpdateAmbassadorFavoriteRequest, UpdateAmbassadorFavoriteResponse>({
        stateKey: STATE_KEYS.UpdateAmbassadorFavorite,
        payload,
        ApiHandler: updateAmbassadorFavorite,
        *onSuccess(response) {
            yield put(updateAmbassadorFavoriteSuccess(payload))

            if (payload.favorited) {
                showSnackBar({
                    title: i18n.t('validation.success.addedToAmbassadorList', {
                        brandName: payload.brand_name,
                    }),
                    type: 'positive',
                })
            } else {
                showSnackBar({
                    title: i18n.t('validation.success.removedToAmbassadorList', {
                        brandName: payload.brand_name,
                    }),
                    type: 'positive',
                })
            }
        },
        *onError() {
            showSnackBar({
                title: i18n.t('errorMessage.businessLogic.contactAccountManagerSubtext'),
                type: 'error',
            })
        },
    })
}

export function* watchUpdateAmbassadorFavoriteFlow(): any {
    while (true) {
        const action = yield take(AmbassadorActions.UPDATE_AMBASSADOR_FAVORITE)
        yield spawn(handleUpdateAmbassadorFavorite, action.payload)
    }
}

export function* handleGetAmbassadorBrands(payload: GetAmbassadorBrandsPayload): any {
    yield handleRequest<GetAmbassadorBrandsRequest, GetAmbassadorBrandsResponse>({
        stateKey: STATE_KEYS.AmbassadorBrands,
        payload,
        ApiHandler: getAmbassadorBrands,
        *onSuccess(response) {
            if (!response.data?.data) return
            yield put(saveAmbassadorBrands(response.data.data.results))
        },
    })
}

export function* watchGetAmbassadorBrandsFlow(): any {
    while (true) {
        const action = yield take(AmbassadorActions.GET_AMBASSADOR_BRANDS)
        yield spawn(handleGetAmbassadorBrands, action.payload)
    }
}

export function* handleGetBrandContentTypes(payload: GetBrandContentTypesPayload): any {
    const request = {
        brands: payload.brands.join(','),
    }

    yield handleRequest<GetBrandContentTypesRequest, BrandContentType[]>({
        stateKey: STATE_KEYS.BrandContentTypes,
        payload: request,
        ApiHandler: getBrandContentTypes,
        *onSuccess(response) {
            if (!response.data) return
            yield put(saveBrandContentTypes(response.data.data))
        },
    })
}

interface GetBrandContentTypeAction extends Action {
    type: typeof AmbassadorActions.GET_BRAND_CONTENT_TYPES
    payload: GetBrandContentTypesPayload
}

export function* watchGetBrandContentTypesFlow(): any {
    yield takeLatest(AmbassadorActions.GET_BRAND_CONTENT_TYPES, function* (action: GetBrandContentTypeAction) {
        yield fork(handleGetBrandContentTypes, action.payload)
    })
}

export function* handleGetAmbassadorFilters(payload: GetAmbassadorFiltersPayload): any {
    const request = {
        brands: payload.brands.join(','),
        content_type: payload.content_type,
    }

    yield handleRequest<GetAmbassadorFiltersRequest, FilterResponse>({
        stateKey: STATE_KEYS.AmbassadorFilters,
        payload: request,
        ApiHandler: getAmbassadorFilters,
        *onSuccess(response) {
            const platform = getPlatformIdFromContentType(payload.content_type)
            if (!response.data?.data.results || !platform) return
            const ambassadorFilters = response.data.data.results.map(filter => {
                if (filter.key === FIELDS_TYPE.SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER) {
                    return {
                        ...filter,
                        value: platform.toString(),
                    }
                }
                return filter
            })
            yield put(saveAmbassadorFilters(ambassadorFilters))
        },
    })
}

export function* watchGetAmbassadorFiltersFlow(): any {
    while (true) {
        const action = yield take(AmbassadorActions.GET_AMBASSADOR_FILTERS)
        yield spawn(handleGetAmbassadorFilters, action.payload)
    }
}
export function* handleGetAmbassadorList(payload: GetAmbassadorListPayload): any {
    const request = {
        brands: payload.brands.join(','),
        content_type: payload.content_type,
        ordering: payload.ordering,
        filteringParams: payload.filteringParams,
    }

    yield handleRequest<GetAmbassadorListRequest, AmbassadorListResponse>({
        stateKey: STATE_KEYS.AmbassadorList,
        payload: request,
        ApiHandler: getAmbassadorList,
        *onSuccess(response) {
            if (!response.data?.data?.results) return
            yield put(saveAmbassadorList(response.data.data.results))
        },
    })
}

export function* watchGetAmbassadorListFlow(): any {
    while (true) {
        const action = yield take(AmbassadorActions.GET_AMBASSADOR_LIST)
        yield spawn(handleGetAmbassadorList, action.payload)
    }
}
